//////////////////////////////////
// IMPORTING MODULES
//////////////////////////////////

import React, { useEffect, useState } from "react";
import { Globe } from "./Globe";
import { AIRPORT_DICTIONARY_INDEX } from "../data/airports";
import { visitedAirportsDemo } from "../data/demoAirports";
import { scrollToSection } from "../lib/utils";

//////////////////////////////////
// COMPONENT: HERO
//////////////////////////////////

const Hero = (): JSX.Element => {
  const [visitedAirports, setVisitedAirports] = useState<any>();

  useEffect(() => {
    const loadAirports = (): void => {
      try {
        const getAirportDetails = (airportCode: string) => {
          return AIRPORT_DICTIONARY_INDEX[airportCode];
        };

        const formattedAirports = visitedAirportsDemo.map((airport) => {
          const airportDetails = getAirportDetails(airport.airport_code);
          return {
            id: airport.id,
            name: airportDetails?.name || "Unknown Airport",
            city: airportDetails?.city || "Unknown City",
            country: airportDetails?.country || "Unknown Country",
            airport_code: airport.airport_code,
            lat: airportDetails?.lat ?? null,
            lng: airportDetails?.lng ?? null,
          };
        });

        setVisitedAirports(formattedAirports);
      } catch (error) {
        console.error("Error loading airports:", error);
      }
    };

    loadAirports();
  }, []);

  return (
    <section
      id="home"
      className="min-h-screen flex items-center justify-center pt-16 px-4"
    >
      <div className="max-w-7xl mx-auto text-center py-20">
        <div
          className="inline-block mb-8"
          style={{ animation: "float 6s ease-in-out infinite" }}
        >
          <div className="w-[400px] sm:w-[500px] h-[500px] md:w-[600px] flex items-center justify-center">
            <Globe
              globeMesh="dayMesh"
              visitedAirports={visitedAirports}
              userSettings={{
                airportsColor: "#2ab8ff",
                connectionsColor: "#b8a11b",
                worldMesh: "dayMesh" as
                  | "dayMesh"
                  | "nightMesh"
                  | "localTimeMesh"
                  | "timeZoneMesh"
                  | "noMesh",
                showGridlines: false,
              }}
            />
          </div>
        </div>

        <h1 className="px-10 text-5xl md:text-7xl font-bold bg-gradient-to-r text-[#2ab8ff]">
          Track Your Travels
        </h1>

        <p className="text-xl md:text-2xl text-gray-300 mb-8 max-w-3xl mx-auto p-10">
          Visualize your journey on an interactive 3D globe. Every airport,
          every country, every adventure beautifully mapped in real-time.
        </p>

        <div className="cursor-pointer flex flex-col sm:flex-row gap-4 justify-center items-center mb-12">
          <button
            onClick={() => scrollToSection("download")}
            className="px-8 py-4 bg-gradient-to-r bg-[#2ab8ff]  text-[#0e1220] rounded-lg hover:shadow-lg font-semibold text-lg"
          >
            Start Tracking Free
          </button>
          <button
            onClick={() => scrollToSection("contact")}
            className="cursor-pointer px-8 py-4 border-2 border-[#2ab8ff] text-[#2ab8ff] rounded-lg hover:bg-[#2ab8ff]/10 transition-all font-semibold text-lg"
          >
            Contact Us
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mt-20">
          <div className="text-center">
            <div className="text-4xl font-bold text-[#2ab8ff] mb-2">
              10,000+
            </div>
            <div className="text-gray-400">Airports Worldwide</div>
          </div>
          <div className="text-center">
            <div className="text-4xl font-bold text-[#2ab8ff] mb-2">195</div>
            <div className="text-gray-400">Countries Supported</div>
          </div>
          <div className="text-center">
            <div className="text-4xl font-bold text-[#2ab8ff] mb-2">
              Real-Time
            </div>
            <div className="text-gray-400">3D Visualization</div>
          </div>
        </div>
      </div>
    </section>
  );
};

//////////////////////////////////
// EXPORTING HERO
//////////////////////////////////

export default Hero;
